import { useEffect, useMemo, useState } from "react";
import { AppButton, FilterBar, FilterDrawer } from "../../../../components/ui";
import {
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import {
  DEFAULT_SORT_RULES,
  FILTER_FIELD_LABELS,
  FUNNEL_FILTER_OPTIONS,
  NEGOTIATION_FILTER_OPTIONS,
  OVERDUE_FILTER_OPTIONS,
  SORT_DIRECTION_OPTIONS,
  SORT_FIELD_OPTIONS,
  type FilterFieldKey,
  type FunnelFilterOption,
  type LloFunnelFiltersState,
  type SortableColumn,
  type SortDirection,
  type SortRule,
} from "./types";
import { countActiveFilters, clearFilterField, hasActiveFilters, resetFilters } from "./filtering";

type FilterOptions = Record<FilterFieldKey, string[]>;

type LloFunnelFiltersProps = {
  filters: LloFunnelFiltersState;
  filterOptions: FilterOptions;
  sortRules: SortRule[];
  loading: boolean;
  downloading: boolean;
  canDownload: boolean;
  onLoad: () => void;
  onApply: (next: LloFunnelFiltersState, sortRules: SortRule[]) => void;
  onDownload: () => void;
  onClear: () => void;
};

const ALL_OPTION = "All";

const stringFilterOptions = createFilterOptions<string>({
  matchFrom: "any",
  stringify: (option) => option,
  trim: true,
  ignoreCase: true,
});

const funnelFilterOptions = createFilterOptions<FunnelFilterOption>({
  matchFrom: "any",
  stringify: (option) => `${option.group} ${option.label} ${option.value}`,
  trim: true,
  ignoreCase: true,
});

function FilterAutocomplete({
  field,
  value,
  options,
  onChange,
}: {
  field: FilterFieldKey;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const optionsWithAll = useMemo(
    () => [ALL_OPTION, ...options.filter((o) => o !== ALL_OPTION)],
    [options]
  );

  return (
    <Autocomplete
      size="small"
      options={optionsWithAll}
      value={value || ALL_OPTION}
      onChange={(_, next) => onChange(!next || next === ALL_OPTION ? "" : next)}
      filterOptions={stringFilterOptions}
      autoHighlight
      clearOnBlur={false}
      handleHomeEndKeys
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={FILTER_FIELD_LABELS[field]}
          placeholder="Type to filter…"
        />
      )}
      fullWidth
    />
  );
}

function FunnelTypeAutocomplete({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const allOption: FunnelFilterOption = { group: "", label: "All", value: ALL_OPTION };
  const options = useMemo(() => [allOption, ...FUNNEL_FILTER_OPTIONS], []);
  const selected =
    !value
      ? allOption
      : FUNNEL_FILTER_OPTIONS.find((option) => option.value === value) ?? null;

  return (
    <Autocomplete
      size="small"
      options={options}
      groupBy={(option) => option.group}
      getOptionLabel={(option) =>
        option.value === ALL_OPTION ? ALL_OPTION : option.value
      }
      isOptionEqualToValue={(a, b) => a.value === b.value}
      value={selected}
      onChange={(_, next: FunnelFilterOption | null) =>
        onChange(!next || next.value === ALL_OPTION ? "" : next.value)
      }
      filterOptions={funnelFilterOptions}
      autoHighlight
      clearOnBlur={false}
      handleHomeEndKeys
      ListboxProps={{ style: { maxHeight: 320 } }}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.value}
          sx={{
            pl:
              option.group === "Main Funnel" && option.value !== ALL_OPTION
                ? "28px !important"
                : undefined,
            fontSize: "0.875rem",
          }}
        >
          {option.label}
        </Box>
      )}
      renderGroup={(params) =>
        params.group ? (
          <li key={params.key}>
            <Typography
              variant="caption"
              component="div"
              sx={{
                px: 2,
                py: 0.75,
                fontWeight: 700,
                color: "text.secondary",
                bgcolor: "grey.50",
                borderBottom: "1px solid",
                borderColor: "divider",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              {params.group}
            </Typography>
            <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
          </li>
        ) : (
          <li key={params.key}>
            <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
          </li>
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={FILTER_FIELD_LABELS.funnel}
          placeholder="Type to filter…"
        />
      )}
      fullWidth
    />
  );
}

function SortRulesEditor({
  rules,
  onChange,
}: {
  rules: SortRule[];
  onChange: (rules: SortRule[]) => void;
}) {
  const usedColumns = useMemo(() => new Set(rules.map((rule) => rule.column)), [rules]);

  const updateRule = (index: number, patch: Partial<SortRule>) => {
    onChange(
      rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule))
    );
  };

  const removeRule = (index: number) => {
    if (rules.length <= 1) return;
    onChange(rules.filter((_, i) => i !== index));
  };

  const addRule = () => {
    const next = SORT_FIELD_OPTIONS.find((option) => !usedColumns.has(option.value));
    if (!next) return;
    onChange([...rules, { column: next.value, direction: "asc" }]);
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>
        Sort by
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        First rule has highest priority
      </Typography>
      <Stack spacing={1}>
        {rules.map((rule, index) => (
          <Stack key={`${rule.column}-${index}`} direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 14 }}>
              {index + 1}.
            </Typography>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id={`llo-sort-col-${index}`}>Column</InputLabel>
              <Select
                labelId={`llo-sort-col-${index}`}
                label="Column"
                value={rule.column}
                onChange={(e) =>
                  updateRule(index, { column: e.target.value as SortableColumn })
                }
              >
                {SORT_FIELD_OPTIONS.filter(
                  (option) => option.value === rule.column || !usedColumns.has(option.value)
                ).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 118 }}>
              <InputLabel id={`llo-sort-dir-${index}`}>Dir</InputLabel>
              <Select
                labelId={`llo-sort-dir-${index}`}
                label="Dir"
                value={rule.direction}
                onChange={(e) =>
                  updateRule(index, { direction: e.target.value as SortDirection })
                }
              >
                {SORT_DIRECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              aria-label="Remove sort rule"
              onClick={() => removeRule(index)}
              disabled={rules.length <= 1}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <AppButton
          size="small"
          startIcon={<AddIcon />}
          onClick={addRule}
          disabled={usedColumns.size >= SORT_FIELD_OPTIONS.length}
          sx={{ alignSelf: "flex-start" }}
        >
          Add sort
        </AppButton>
      </Stack>
    </Box>
  );
}

function buildActiveChips(filters: LloFunnelFiltersState) {
  const chips: { key: keyof LloFunnelFiltersState; label: string }[] = [];
  (
    [
      "caseName",
      "raa",
      "ap",
      "negotiation",
      "funnel",
      "taskName",
      "taskAssignee",
      "overdue",
    ] as FilterFieldKey[]
  ).forEach((field) => {
    if (filters[field]) {
      chips.push({
        key: field,
        label: `${FILTER_FIELD_LABELS[field]}: ${filters[field]}`,
      });
    }
  });
  if (filters.lloExpirationFrom) {
    chips.push({
      key: "lloExpirationFrom",
      label: `LLO exp from: ${filters.lloExpirationFrom.format("MM/DD/YYYY")}`,
    });
  }
  if (filters.lloExpirationTo) {
    chips.push({
      key: "lloExpirationTo",
      label: `LLO exp to: ${filters.lloExpirationTo.format("MM/DD/YYYY")}`,
    });
  }
  if (filters.dueDateFrom) {
    chips.push({
      key: "dueDateFrom",
      label: `Due from: ${filters.dueDateFrom.format("MM/DD/YYYY")}`,
    });
  }
  if (filters.dueDateTo) {
    chips.push({
      key: "dueDateTo",
      label: `Due to: ${filters.dueDateTo.format("MM/DD/YYYY")}`,
    });
  }
  return chips;
}

function optionsForField(field: FilterFieldKey, filterOptions: FilterOptions): string[] {
  if (field === "negotiation") return [...NEGOTIATION_FILTER_OPTIONS];
  if (field === "overdue") return [...OVERDUE_FILTER_OPTIONS];
  return filterOptions[field] ?? [];
}

function DateRangeFields({
  label,
  from,
  to,
  onFromChange,
  onToChange,
}: {
  label: string;
  from: Dayjs | null;
  to: Dayjs | null;
  onFromChange: (value: Dayjs | null) => void;
  onToChange: (value: Dayjs | null) => void;
}) {
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1.25}>
        <DatePicker
          label="From"
          value={from}
          onChange={onFromChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
        <DatePicker
          label="To"
          value={to}
          onChange={onToChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
      </Stack>
    </Box>
  );
}

export function LloFunnelFilters({
  filters,
  filterOptions,
  sortRules,
  loading,
  downloading,
  canDownload,
  onLoad,
  onApply,
  onDownload,
  onClear,
}: LloFunnelFiltersProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState<LloFunnelFiltersState>(filters);
  const [draftSortRules, setDraftSortRules] = useState<SortRule[]>(sortRules);
  const filtersActive = hasActiveFilters(filters);
  const draftActive = hasActiveFilters(draft);
  const activeCount = countActiveFilters(filters);
  const activeChips = buildActiveChips(filters);

  useEffect(() => {
    if (drawerOpen) {
      setDraft(filters);
      setDraftSortRules(sortRules.length > 0 ? sortRules : [...DEFAULT_SORT_RULES]);
    }
  }, [drawerOpen, filters, sortRules]);

  const patchDraft = (patch: Partial<LloFunnelFiltersState>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const clearField = (key: keyof LloFunnelFiltersState) => {
    onApply(clearFilterField(filters, key), sortRules);
  };

  const handleApply = () => {
    onApply(draft, draftSortRules.length > 0 ? draftSortRules : [...DEFAULT_SORT_RULES]);
    setDrawerOpen(false);
  };

  const clearDraft = () => setDraft(resetFilters());

  return (
    <>
      <FilterBar
        leading={
          <>
            <AppButton variant="contained" size="small" onClick={onLoad} disabled={loading}>
              {loading ? <CircularProgress size={18} color="inherit" /> : "Load Report"}
            </AppButton>
            <AppButton
              variant="outlined"
              size="small"
              startIcon={downloading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              onClick={onDownload}
              disabled={downloading || loading || !canDownload}
              sx={{ bgcolor: "background.paper" }}
            >
              Download
            </AppButton>
          </>
        }
        activeCount={activeCount}
        filtersActive={filtersActive}
        onOpenFilters={() => setDrawerOpen(true)}
        onClear={onClear}
        clearDisabled={loading || !filtersActive}
        chips={activeChips.map((chip) => ({
          key: String(chip.key),
          label: chip.label,
          onDelete: () => clearField(chip.key),
        }))}
      />

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Filters & sort"
        subtitle="Apply to reload the report with these filters"
        activeCount={activeCount}
        actions={
          <>
            <AppButton
              variant="outlined"
              color="warning"
              fullWidth
              startIcon={<FilterAltOffIcon />}
              onClick={clearDraft}
              disabled={!draftActive}
            >
              Clear filters
            </AppButton>
            <AppButton variant="contained" fullWidth onClick={handleApply} disabled={loading}>
              {loading ? <CircularProgress size={18} color="inherit" /> : "Apply"}
            </AppButton>
          </>
        }
      >
        <Stack spacing={1.75}>
            <FilterAutocomplete
              field="caseName"
              value={draft.caseName}
              options={optionsForField("caseName", filterOptions)}
              onChange={(value) => patchDraft({ caseName: value })}
            />
            <FilterAutocomplete
              field="raa"
              value={draft.raa}
              options={optionsForField("raa", filterOptions)}
              onChange={(value) => patchDraft({ raa: value })}
            />
            <FilterAutocomplete
              field="ap"
              value={draft.ap}
              options={optionsForField("ap", filterOptions)}
              onChange={(value) => patchDraft({ ap: value })}
            />
            <FilterAutocomplete
              field="negotiation"
              value={draft.negotiation}
              options={optionsForField("negotiation", filterOptions)}
              onChange={(value) => patchDraft({ negotiation: value })}
            />
            <FunnelTypeAutocomplete
              value={draft.funnel}
              onChange={(value) => patchDraft({ funnel: value })}
            />
            <FilterAutocomplete
              field="taskName"
              value={draft.taskName}
              options={optionsForField("taskName", filterOptions)}
              onChange={(value) => patchDraft({ taskName: value })}
            />
            <FilterAutocomplete
              field="taskAssignee"
              value={draft.taskAssignee}
              options={optionsForField("taskAssignee", filterOptions)}
              onChange={(value) => patchDraft({ taskAssignee: value })}
            />
            <FilterAutocomplete
              field="overdue"
              value={draft.overdue}
              options={optionsForField("overdue", filterOptions)}
              onChange={(value) => patchDraft({ overdue: value })}
            />
            <DateRangeFields
              label="LLO Expiration"
              from={draft.lloExpirationFrom}
              to={draft.lloExpirationTo}
              onFromChange={(value) => patchDraft({ lloExpirationFrom: value })}
              onToChange={(value) => patchDraft({ lloExpirationTo: value })}
            />
            <DateRangeFields
              label="Due Date"
              from={draft.dueDateFrom}
              to={draft.dueDateTo}
              onFromChange={(value) => patchDraft({ dueDateFrom: value })}
              onToChange={(value) => patchDraft({ dueDateTo: value })}
            />
            <SortRulesEditor rules={draftSortRules} onChange={setDraftSortRules} />
        </Stack>
      </FilterDrawer>
    </>
  );
}
